
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { checkIsAdmin } from '@/utils/admin-helpers';
import { 
  Sidebar,
  SidebarProvider,
  SidebarTrigger,
  SidebarContent,
  SidebarInset
} from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import AdminSidebarContent from '@/components/admin/sidebar/AdminSidebarContent';
import { AdminSidebarFooter } from '@/components/admin/sidebar/AdminSidebarFooter';
import { AdminSidebarHeader } from '@/components/admin/sidebar/AdminSidebarHeader';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Home, ArrowLeft, Calendar, Users, Settings } from 'lucide-react';
import { currentVersion } from '@/config/version-info';
import { Badge } from '@/components/ui/badge';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    const verifyAdmin = async () => {
      setIsLoading(true);
      if (!user) {
        navigate('/admin');
        return;
      }

      const adminStatus = await checkIsAdmin(user.id);
      setIsAdmin(adminStatus);

      if (!adminStatus) {
        toast({
          variant: 'destructive',
          title: 'Access Denied',
          description: 'You do not have administrator privileges.',
        });
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
      
      setIsLoading(false);
    };

    verifyAdmin();
  }, [user, navigate, toast]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    toast({
      title: 'Logged Out',
      description: 'You have successfully logged out of your administrator account',
    });
    navigate('/admin');
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="space-y-4 w-[300px]">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-[300px] w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
          <p className="mt-2 text-slate-600">You do not have administrator privileges.</p>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-gray-50/50">
        <Sidebar variant="sidebar" collapsible="icon" className="bg-white shadow-sm border-r">
          <AdminSidebarHeader />
          <SidebarContent>
            <AdminSidebarContent />
          </SidebarContent>
          <AdminSidebarFooter onSignOut={handleSignOut} userEmail={user?.email} />
        </Sidebar>

        <SidebarInset className="flex-1 flex flex-col min-h-screen">
          <header className="sticky top-0 z-40 bg-white border-b h-16 flex items-center justify-between px-2 sm:px-4 shadow-sm">
            <div className="flex items-center">
              <SidebarTrigger className="mr-2 sm:mr-4" />
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900">Admin Console</h1>
              <Link to="/admin/version">
                <Badge variant="outline" className="ml-3 cursor-pointer hover:bg-gray-50">
                  v{currentVersion.version}
                </Badge>
              </Link>
            </div>
            
            {/* Main Website Navigation - Mobile Optimized */}
            {isMobile ? (
              <div className="flex items-center space-x-1">
                <Button variant="outline" size="sm" asChild>
                  <Link to="/" className="flex items-center px-2">
                    <Home className="h-4 w-4" />
                  </Link>
                </Button>
                
                <Button variant="outline" size="sm" asChild>
                  <Link to="/booking-choice" className="flex items-center px-2">
                    <Calendar className="h-4 w-4" />
                  </Link>
                </Button>
                
                <Button variant="outline" size="sm" asChild>
                  <Link to="/news-events" className="flex items-center px-2">
                    <Users className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <Button variant="outline" size="sm" asChild>
                  <Link to="/" className="flex items-center">
                    <Home className="h-4 w-4 mr-2" />
                    Homepage
                  </Link>
                </Button>
                
                <Button variant="outline" size="sm" asChild>
                  <Link to="/booking-choice" className="flex items-center">
                    Book Appointment
                  </Link>
                </Button>
                
                <Button variant="outline" size="sm" asChild>
                  <Link to="/news-events" className="flex items-center">
                    News & Events
                  </Link>
                </Button>
              </div>
            )}
          </header>
          
          <main className="flex-1 p-3 sm:p-6 pt-6">
            {children}
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
};

export default AdminLayout;
